/* eslint-disable react/require-default-props */
import React, { useEffect, useState } from 'react';
import { InsuranceQuote } from 'store/modules/conlicitaseg_store/types';
import { downloadFile } from 'services/actions/conlicitaseg_actions';

interface QuoteDownloadBlockProps {
  quote: InsuranceQuote | undefined;
  inline?: boolean;
  className?: string;
}

const QuoteDownloadBlock: React.FC<QuoteDownloadBlockProps> = ({
  quote,
  inline = false,
  className = '',
}) => {
  const [isQuoteDownloading, setIsQuoteDownloading] = useState<boolean>(false);
  const [quoteDownloadError, setQuoteDownloadError] = useState<boolean>(false);
  const [isDraftDownloading, setIsDraftDownloading] = useState<boolean>(false);
  const [downloadError, setDownloadError] = useState<boolean>(false);

  useEffect(() => {
    setQuoteDownloadError(false);
    setDownloadError(false);
  }, [quote?.id]);

  const getCompanyDisplayName = (companyId: string | undefined): string => {
    if (!companyId) return '';
    const names: { [key: string]: string } = {
      junto: 'Junto',
      pottencial: 'Pottencial',
      sombrero: 'Sombrero Seguradora',
      avla: 'Avla Seguradora',
    };
    return names[companyId.toLowerCase()] || companyId;
  };

  const handleDownloadQuote = async () => {
    if (!quote) return;
    setIsQuoteDownloading(true);
    setQuoteDownloadError(false);
    try {
      await downloadFile(quote.id, 'cotacao');
    } catch (error) {
      setQuoteDownloadError(true);
    } finally {
      setIsQuoteDownloading(false);
    }
  };

  const handleDownloadDraft = async () => {
    if (!quote) return;
    setIsDraftDownloading(true);
    setDownloadError(false);
    try {
      await downloadFile(quote.id, 'minuta');
    } catch (error) {
      setDownloadError(true);
    } finally {
      setIsDraftDownloading(false);
    }
  };

  if (!quote) return null;

  const alertClass = inline
    ? 'alert alert-info d-flex justify-content-between align-items-center mb-4 p-2'
    : 'alert alert-info p-3 mb-0';

  const buttonsWrapperClass = inline ? 'd-flex' : 'd-flex flex-wrap';
  const textWrapperClass = inline ? '' : 'mb-2';
  const errorAlertClass = inline ? 'alert alert-danger mb-4' : 'alert alert-danger mt-2 mb-0';

  return (
    <div className={className}>
      <div className={alertClass}>
        <div className={textWrapperClass}>
          Você pode baixar a cotação e a minuta da seguradora{' '}
          <strong>{getCompanyDisplayName(quote.insurance_company_id)}</strong>.
        </div>
        <div className={buttonsWrapperClass}>
          <button
            type="button"
            className={`btn btn-info btn-sm mr-2 ${inline ? '' : 'mb-1'}`}
            disabled={isQuoteDownloading || !quote.url_quote}
            onClick={handleDownloadQuote}
          >
            {isQuoteDownloading ? (
              <>
                <span
                  className="spinner-border spinner-border-sm mr-2"
                  role="status"
                  aria-label="Baixando"
                />
                Baixando...
              </>
            ) : (
              'Baixar cotação'
            )}
          </button>
          <button
            type="button"
            className={`btn btn-info btn-sm ${inline ? '' : 'mb-1'}`}
            disabled={isDraftDownloading || !quote.draft_url}
            onClick={handleDownloadDraft}
          >
            {isDraftDownloading ? (
              <>
                <span
                  className="spinner-border spinner-border-sm mr-2"
                  role="status"
                  aria-label="Baixando"
                />
                Baixando...
              </>
            ) : (
              'Baixar minuta'
            )}
          </button>
        </div>
      </div>
      {quoteDownloadError && (
        <div className={errorAlertClass} role="alert">
          Ocorreu um erro ao baixar a cotação.
        </div>
      )}
      {downloadError && (
        <div className={errorAlertClass} role="alert">
          Ocorreu um erro ao baixar a minuta.
        </div>
      )}
    </div>
  );
};

export default QuoteDownloadBlock;
